(() => {
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const zoomFactor = 2;

  class ProductImageZoom {
    constructor(stage) {
      this.stage = stage;
      this.overlay = null;
      this.sourceImage = null;
      this.lastPointerEvent = null;
      this.removeTimer = null;
      this.frame = null;

      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);

      if ('PointerEvent' in window) {
        stage.addEventListener('pointerenter', this.onPointerEnter);
        stage.addEventListener('pointermove', this.onPointerMove);
        stage.addEventListener('pointerleave', this.onPointerLeave);
      } else {
        stage.addEventListener('mouseenter', this.onPointerEnter);
        stage.addEventListener('mousemove', this.onPointerMove);
        stage.addEventListener('mouseleave', this.onPointerLeave);
      }
      window.addEventListener('resize', this.onViewportChange, { passive: true });
    }

    getImage() {
      return this.stage.querySelector('img:not(.uis24-product-image-zoom__image)');
    }

    canZoom(event) {
      // The media query is the source of truth for capability. Some desktop
      // browsers do not expose a pointerType on synthetic or assistive input.
      // Touch pointers remain excluded so the gallery keeps its native gestures.
      return finePointer.matches && event.pointerType !== 'touch';
    }

    onPointerEnter(event) {
      if (!this.canZoom(event)) return;

      const image = this.getImage();
      if (!image || !(image.currentSrc || image.src)) return;

      this.lastPointerEvent = event;
      this.sourceImage = image;
      if (this.overlay) {
        this.updatePosition(event);
        return;
      }
      this.createOverlay(image);
    }

    createOverlay(image) {
      this.removeOverlay(true);
      this.sourceImage = image;

      const overlay = document.createElement('img');
      overlay.className = 'uis24-product-image-zoom__image';
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.decoding = 'async';
      overlay.draggable = false;
      overlay.src = image.currentSrc || image.src;

      this.overlay = overlay;
      this.stage.append(overlay);

      const reveal = () => {
        if (this.overlay !== overlay || !this.lastPointerEvent) return;
        this.stage.classList.add('is-uis24-zooming', 'is-uis24-zoom-ready');
        this.updatePosition(this.lastPointerEvent);
      };

      if (overlay.complete) {
        reveal();
      } else {
        overlay.addEventListener('load', reveal, { once: true });
        overlay.addEventListener('error', () => this.removeOverlay(true), { once: true });
      }
    }

    onPointerMove(event) {
      if (!this.canZoom(event) || !this.overlay) return;
      this.lastPointerEvent = event;
      if (this.frame) return;

      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        if (this.lastPointerEvent) this.updatePosition(this.lastPointerEvent);
      });
    }

    onPointerLeave() {
      this.lastPointerEvent = null;
      this.removeOverlay();
    }

    onViewportChange() {
      if (this.overlay && this.lastPointerEvent) this.updatePosition(this.lastPointerEvent);
    }

    updatePosition(event) {
      if (!this.overlay || !this.sourceImage) return;

      const stageRect = this.stage.getBoundingClientRect();
      const imageRect = this.sourceImage.getBoundingClientRect();
      if (!stageRect.width || !stageRect.height || !imageRect.width || !imageRect.height) return;

      const imageX = Math.min(Math.max(event.clientX - imageRect.left, 0), imageRect.width);
      const imageY = Math.min(Math.max(event.clientY - imageRect.top, 0), imageRect.height);
      const sourceX = imageX / imageRect.width;
      const sourceY = imageY / imageRect.height;
      const scaledWidth = imageRect.width * zoomFactor;
      const scaledHeight = imageRect.height * zoomFactor;
      const pointerX = event.clientX - stageRect.left;
      const pointerY = event.clientY - stageRect.top;

      const overlayWidth = `${scaledWidth}px`;
      const overlayHeight = `${scaledHeight}px`;
      if (this.overlay.style.width !== overlayWidth) this.overlay.style.width = overlayWidth;
      if (this.overlay.style.height !== overlayHeight) this.overlay.style.height = overlayHeight;
      this.overlay.style.transform = `translate3d(${pointerX - sourceX * scaledWidth}px, ${pointerY - sourceY * scaledHeight}px, 0)`;
    }

    removeOverlay(immediate = false) {
      const overlay = this.overlay;
      this.overlay = null;
      this.sourceImage = null;
      this.stage.classList.remove('is-uis24-zooming', 'is-uis24-zoom-ready');
      window.clearTimeout(this.removeTimer);
      window.cancelAnimationFrame(this.frame);
      this.frame = null;

      if (!overlay) return;
      const remove = () => overlay.remove();
      if (immediate) {
        remove();
        return;
      }

      overlay.addEventListener('transitionend', remove, { once: true });
      this.removeTimer = window.setTimeout(remove, 180);
    }
  }

  const enableProductImageZoom = () => {
    if (!finePointer.matches) return;

    document
      .querySelectorAll('product-info media-gallery [id^="GalleryViewer"] .product-media-container.media-type-image .product__media')
      .forEach((stage) => {
        if (!stage.dataset.uis24ZoomBound) {
          stage.dataset.uis24ZoomBound = 'true';
          new ProductImageZoom(stage);
        }
      });
  };

  // Keep the existing product-info media refresh hook working after a variant
  // swaps its featured image. The legacy function name is used by the theme.
  window.enableZoomOnHover = enableProductImageZoom;
  enableProductImageZoom();
  finePointer.addEventListener?.('change', enableProductImageZoom);
})();
